import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface DensityReportValues {
  specificGravity?: number;
  densityPpg?: number;
}

@Component({
  selector: 'app-density-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './density-test-report.component.html',
  styleUrl: './density-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DensityTestReportComponent {
  private static readonly WATER_DENSITY_PPG = 8.345;

  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected densityReportValues(): DensityReportValues | null {
    const parsedSpecificGravity = this.parsedValue([
      'specificgravity',
      'densitygml',
      'densitygrampermilliliter'
    ], this.output) ?? this.parsedValue([
      'specificgravity',
      'densitygml',
      'densitygrampermilliliter'
    ], this.test);
    const parsedDensityPpg = this.parsedValue([
      'densitylbgal',
      'densitylbpergal',
      'densityppg',
      'densitylbpergallon'
    ], this.output) ?? this.parsedValue([
      'densitylbgal',
      'densitylbpergal',
      'densityppg',
      'densitylbpergallon'
    ], this.test);
    const xmlSpecificGravity = this.xmlValue(['specificgravity', 'densitygml'], this.output)
      ?? this.xmlValue(['specificgravity', 'densitygml'], this.test);
    const xmlDensity = this.xmlValue(['density'], this.output)
      ?? this.xmlValue(['density'], this.test);

    const specificGravity = this.toNumber(parsedSpecificGravity ?? xmlSpecificGravity);
    const directDensity = this.toNumber(parsedDensityPpg ?? xmlDensity);
    const densityPpg = directDensity !== undefined && directDensity > 3
      ? directDensity
      : undefined;
    const resolvedSpecificGravity = specificGravity
      ?? (directDensity !== undefined && directDensity <= 3 ? directDensity : undefined)
      ?? (densityPpg !== undefined ? densityPpg / DensityTestReportComponent.WATER_DENSITY_PPG : undefined);
    const resolvedDensityPpg = densityPpg
      ?? (resolvedSpecificGravity !== undefined
        ? resolvedSpecificGravity * DensityTestReportComponent.WATER_DENSITY_PPG
        : undefined);

    if (resolvedSpecificGravity === undefined && resolvedDensityPpg === undefined) return null;

    return {
      specificGravity: resolvedSpecificGravity,
      densityPpg: resolvedDensityPpg
    };
  }

  protected formatSpecificGravity(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(3);
  }

  protected formatDensityPpg(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(2);
  }

  private parsedValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | null {
    const acceptedNames = new Set(names);
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const field = fields.find(item =>
      acceptedNames.has(this.normalize(item.label)));
    return field?.value?.trim() || null;
  }

  private xmlValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | null {
    if (!source.content) return null;

    const document = new DOMParser().parseFromString(source.content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    const acceptedNames = new Set(names);
    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        const key = this.normalize(element.getAttribute('key'));
        if (!acceptedNames.has(key)) continue;
      } else if (!acceptedNames.has(element.localName.toLowerCase())) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}